import { useState, useEffect, useMemo, useCallback } from 'react';
import { Eye, FileText, Mail, Trash2, CheckCircle } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { PurchaseOrderFundicionWithLines, Location } from '../types';
import Modal from './Modal';
import StockChangeModal from './StockChangeModal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData, EmptyStateError } from './EmptyState';
import CommentsSection from './CommentsSection';
import { formatDate, formatDateTime, getMadridDate, toDateInputValue } from '../utils/dateFormatter';
import SearchableSelect from './SearchableSelect';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (stock < minStock) {
    return 'var(--error)'; // Rojo: por debajo del mínimo
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)'; // Amarillo: igual o hasta 20% por encima del mínimo
  } else {
    return 'var(--success)'; // Verde: más de 20% por encima del mínimo
  }
};

// Función helper para verificar si mostrar icono de alerta (stock < 50% del mínimo)
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  return stock < minStock * 0.5;
};

interface Need {
  metal_piece_id: number;
  cod_pieza: string;
  nombre_pieza: string;
  material_base: 'PLATA' | 'LATON';
  current_stock_taller: number;
  min_stock_taller: number;
  min_taller_only?: number;
  downstream_need?: number;
  needed: number;
}

interface MetalPiece {
  id: number;
  cod_pieza: string;
  nombre_pieza: string;
  material_base: 'PLATA' | 'LATON';
}

export default function PurchaseOrdersFundicion() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [orders, setOrders] = useState<PurchaseOrderFundicionWithLines[]>([]);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [metalPieces, setMetalPieces] = useState<MetalPiece[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrderFundicionWithLines | null>(null);
  const [selectedLines, setSelectedLines] = useState<Map<number, number>>(new Map());
  const [formData, setFormData] = useState({ fundidor: '', expected_at: '' });
  const [emailFormData, setEmailFormData] = useState({
    to: 'cesar@uniquebrand.es',
    cc: 'cesar@uniquebrand.es',
    subject: '',
    body: `Buenos dias Gerard, te adjunto la peticion de fundicion para esta semana.

Cualquier cosa me comentas.

Un saludo,`
  });
  const [lineQtyInputs, setLineQtyInputs] = useState<Map<number, string>>(new Map());
  const [detailSelectedLines, setDetailSelectedLines] = useState<Set<number>>(new Set());
  const [manuallyAddedPieces, setManuallyAddedPieces] = useState<Set<number>>(new Set());
  const [isStockChangeModalOpen, setIsStockChangeModalOpen] = useState(false);
  const [stockChanges, setStockChanges] = useState<Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }>>([]);
  const [isReceivingAll, setIsReceivingAll] = useState(false);
  const [isReceivingSelected, setIsReceivingSelected] = useState(false);
  const [selectedPieceTypes, setSelectedPieceTypes] = useState<Set<string>>(new Set(['ANILLO', 'COLGANTE', 'PULSERA', 'REASA']));

  // Función helper para determinar el tipo de pieza basándose en el código
  const getPieceType = (codigo: string): 'ANILLO' | 'COLGANTE' | 'PULSERA' | 'REASA' | null => {
    if (!codigo || typeof codigo !== 'string') return null;

    const cleaned = codigo.trim().toUpperCase();
    if (cleaned.length === 0) return null;

    const firstChar = cleaned.charAt(0);

    if (firstChar === 'A') return 'ANILLO';
    if (firstChar === 'C') return 'COLGANTE';
    if (firstChar === 'P') return 'PULSERA';
    if (firstChar === 'R') return 'REASA';

    return null;
  };

  const togglePieceType = (type: string) => {
    setSelectedPieceTypes((prev) => {
      const newTypes = new Set(prev);
      if (newTypes.has(type)) {
        newTypes.delete(type);
      } else {
        newTypes.add(type);
      }
      return newTypes;
    });
  };

  // Filtrar necesidades: mostrar solo las que tienen necesidad O las agregadas manualmente
  // Y que coincidan con los tipos de pieza seleccionados
  const visibleNeeds = useMemo(() => {
    return needs.filter(need => {
      const hasNeed = need.needed > 0;
      const isManuallyAdded = manuallyAddedPieces.has(need.metal_piece_id);
      if (!hasNeed && !isManuallyAdded) return false;

      // Filtrar por tipo de pieza
      const pieceType = getPieceType(need.cod_pieza);
      if (pieceType && !selectedPieceTypes.has(pieceType)) return false;

      return true;
    });
  }, [needs, manuallyAddedPieces, selectedPieceTypes]);

  // Filtrar piezas metálicas por tipo también
  const filteredMetalPieces = useMemo(() => {
    return metalPieces.filter(piece => {
      const pieceType = getPieceType(piece.cod_pieza);
      if (pieceType && !selectedPieceTypes.has(pieceType)) return false;
      return true;
    });
  }, [metalPieces, selectedPieceTypes]);

  const selectableNeedIds = visibleNeeds.map((need) => need.metal_piece_id);
  const selectablePieceIds = filteredMetalPieces.map((piece) => piece.id);
  const allNeedsSelected =
    selectableNeedIds.length > 0 && selectableNeedIds.every((id) => selectedLines.has(id));
  const allPiecesSelected =
    selectablePieceIds.length > 0 && selectablePieceIds.every((id) => selectedLines.has(id));

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadNeeds = useCallback(async (): Promise<Need[]> => {
    try {
      const response = await api.get('/purchase-orders-fundicion/needs');
      setNeeds(response.data);
      return response.data;
    } catch (err: any) {
      showError('Error al cargar necesidades');
      return [];
    }
  }, [api]);

  useEffect(() => {
    const handleMultiplierUpdated = () => {
      loadNeeds();
    };
    window.addEventListener('multiplier-updated', handleMultiplierUpdated);
    return () => {
      window.removeEventListener('multiplier-updated', handleMultiplierUpdated);
    };
  }, [loadNeeds]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Usar endpoint combinado para cargar todos los datos en una sola llamada
      const response = await api.get('/purchase-orders-fundicion/initial-data');
      setOrders(response.data.orders);
      setNeeds(response.data.needs);
      setMetalPieces(response.data.metalPieces.filter((p: MetalPiece) => p.material_base === 'PLATA' || p.material_base === 'LATON'));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error al cargar datos iniciales';
      showError(errorMessage);
      setError(errorMessage);
      // Fallback a carga individual si el endpoint combinado falla
      loadOrders();
      loadNeeds();
      loadMetalPieces();
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/purchase-orders-fundicion');
      setOrders(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error al cargar pedidos';
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const loadMetalPieces = async () => {
    try {
      const response = await api.get('/metal-pieces');
      setMetalPieces(response.data.filter((p: MetalPiece) => p.material_base === 'PLATA' || p.material_base === 'LATON'));
    } catch (err: any) {
      showError('Error al cargar piezas metálicas');
    }
  };

  const handleManualAddPiece = async (piece: MetalPiece) => {
    // Verificar si la pieza ya está en needs
    const existingNeed = needs.find(n => n.metal_piece_id === piece.id);
    if (existingNeed) {
      // Si ya existe, solo marcarla como agregada manualmente
      setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
      // Seleccionar con cantidad sugerida
      const newMap = new Map(selectedLines);
      newMap.set(piece.id, existingNeed.needed || 10);
      setSelectedLines(newMap);
    } else {
      // Si no existe, consultar stock real del backend
      try {
        // Cargar ubicaciones si no están cargadas
        let allLocations = locations;
        if (allLocations.length === 0) {
          const locationsResponse = await api.get('/locations');
          allLocations = locationsResponse.data;
          setLocations(allLocations);
        }

        const tallerPlata = allLocations.find((l: Location) => l.code === 'TALLER_PLATA');
        const tallerLaton = allLocations.find((l: Location) => l.code === 'TALLER_LATON');
        const tallerLocationId = piece.material_base === 'PLATA' ? tallerPlata?.id : tallerLaton?.id;

        // Consultar stock real
        const stockResponse = await api.get('/stock', {
          params: {
            item_type: 'METAL',
            item_id: piece.id,
            location_id: tallerLocationId
          }
        });
        const stockData = stockResponse.data;
        const currentStock = Array.isArray(stockData) && stockData.length > 0
          ? parseFloat(stockData[0].qty) || 0
          : 0;

        // Consultar mínimo de stock
        const minStockResponse = await api.get('/min-stock', {
          params: {
            item_type: 'METAL',
            item_id: piece.id,
            location_id: tallerLocationId
          }
        });
        const minStockData = minStockResponse.data;
        const minStock = Array.isArray(minStockData) && minStockData.length > 0
          ? parseFloat(minStockData[0].min_qty_base) || 0
          : 0;

        // Obtener multiplicador
        const multiplierResponse = await api.get('/settings/min-stock-multiplier');
        const multiplier = parseFloat(multiplierResponse.data.value || '1');
        const effectiveMin = Math.round(minStock * multiplier);

        // Crear necesidad con valores reales
        const newNeed: Need = {
          metal_piece_id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          material_base: piece.material_base,
          current_stock_taller: currentStock,
          min_stock_taller: effectiveMin,
          min_taller_only: effectiveMin,
          downstream_need: 0,
          needed: Math.max(0, effectiveMin - currentStock) || 10, // Cantidad sugerida basada en necesidad real
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, newNeed.needed);
        setSelectedLines(newMap);
      } catch (err: any) {
        // Fallback: crear necesidad con valores por defecto
        const newNeed: Need = {
          metal_piece_id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          material_base: piece.material_base,
          current_stock_taller: 0,
          min_stock_taller: 0,
          needed: 10,
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, piece.id]));
        const newMap = new Map(selectedLines);
        newMap.set(piece.id, 10);
        setSelectedLines(newMap);
      }
    }
    showSuccess(`${piece.cod_pieza} agregado manualmente`);
  };

  const handleOpenCreateModal = async () => {
    setSelectedLines(new Map());
    setManuallyAddedPieces(new Set());

    // Pre-llenar fundidor con "Microfusion Garre"
    // Pre-llenar fecha esperada con el viernes de la siguiente semana
    const today = new Date();
    const currentDay = today.getDay(); // 0 = domingo, 5 = viernes

    // Calcular días hasta el viernes de la siguiente semana
    // Primero calculamos días hasta el próximo viernes, luego sumamos 7 días
    let daysToNextFriday = 0;
    if (currentDay <= 5) {
      // Si es de lunes a viernes, el próximo viernes es en (5 - currentDay) días
      daysToNextFriday = 5 - currentDay;
    } else {
      // Si es sábado (6) o domingo (0), el próximo viernes es en (5 + 7 - currentDay) días
      daysToNextFriday = 5 + 7 - currentDay;
    }

    // El viernes de la siguiente semana es el próximo viernes + 7 días (en zona horaria de Madrid)
    const madridToday = getMadridDate();
    const nextWeekFriday = new Date(madridToday);
    nextWeekFriday.setDate(madridToday.getDate() + daysToNextFriday + 7);
    const nextWeekFridayStr = toDateInputValue(nextWeekFriday);

    setFormData({ fundidor: 'Microfusion Garre', expected_at: nextWeekFridayStr });
    await loadNeeds();
    setIsCreateModalOpen(true);
  };

  const handleToggleLine = (need: Need) => {
    const newMap = new Map(selectedLines);
    if (newMap.has(need.metal_piece_id)) {
      newMap.delete(need.metal_piece_id);
    } else {
      newMap.set(need.metal_piece_id, need.needed);
    }
    setSelectedLines(newMap);
  };

  const handleToggleMetalPiece = (piece: MetalPiece) => {
    const newMap = new Map(selectedLines);
    if (newMap.has(piece.id)) {
      newMap.delete(piece.id);
    } else {
      // Pre-llenar con 10 por defecto si no hay necesidad calculada
      newMap.set(piece.id, 10);
    }
    setSelectedLines(newMap);
  };

  const handleUpdateQty = (metalPieceId: number, qty: number) => {
    const newMap = new Map(selectedLines);
    if (qty > 0) {
      newMap.set(metalPieceId, qty);
    } else {
      newMap.delete(metalPieceId);
    }
    setSelectedLines(newMap);
  };

  const toggleSelectAllNeeds = () => {
    const newMap = new Map(selectedLines);
    if (allNeedsSelected) {
      selectableNeedIds.forEach((id) => newMap.delete(id));
    } else {
      selectableNeedIds.forEach((id) => {
        const need = needs.find((n) => n.metal_piece_id === id);
        if (need) {
          newMap.set(id, need.needed);
        }
      });
    }
    setSelectedLines(newMap);
  };

  const toggleSelectAllPieces = () => {
    const newMap = new Map(selectedLines);
    if (allPiecesSelected) {
      selectablePieceIds.forEach((id) => newMap.delete(id));
    } else {
      filteredMetalPieces.forEach((piece) => {
        newMap.set(piece.id, selectedLines.get(piece.id) ?? 10);
      });
    }
    setSelectedLines(newMap);
  };

  const handleCreateOrder = async () => {
    if (selectedLines.size === 0) {
      showError('Debes seleccionar al menos una línea');
      return;
    }
    if (!formData.fundidor.trim()) {
      showError('El nombre del fundidor es obligatorio');
      return;
    }

    try {
      const lines = Array.from(selectedLines.entries()).map(([metal_piece_id, qty_ordered]) => ({
        metal_piece_id,
        qty_ordered,
      }));

      await api.post('/purchase-orders-fundicion', {
        fundidor: formData.fundidor,
        expected_at: formData.expected_at || undefined,
        lines,
      });
      showSuccess('Pedido de fundición creado correctamente');
      setIsCreateModalOpen(false);
      setSelectedLines(new Map());
      loadOrders();
      loadNeeds();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear pedido');
    }
  };

  const initializeDetailLineState = (orderData: PurchaseOrderFundicionWithLines) => {
    const qtyInputs = new Map<number, string>();
    const selectedSet = new Set<number>();
    orderData.lines.forEach((line) => {
      const pending = line.qty_ordered - line.qty_received;
      if (pending > 0) {
        qtyInputs.set(line.id, lineQtyInputs.get(line.id) || pending.toString());
        selectedSet.add(line.id);
      }
    });
    setLineQtyInputs(qtyInputs);
    setDetailSelectedLines(selectedSet);
  };

  const refreshDetailOrder = async (orderId: number) => {
    const response = await api.get(`/purchase-orders-fundicion/${orderId}`);
    const orderData = response.data;
    initializeDetailLineState(orderData);
    setSelectedOrder(orderData);
  };

  const handleOpenDetailModal = async (order: PurchaseOrderFundicionWithLines) => {
    try {
      await refreshDetailOrder(order.id);
      setIsDetailModalOpen(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar detalles');
    }
  };

  const handleReceiveLine = async (orderId: number, lineId: number, qty: number) => {
    if (qty <= 0) {
      showError('La cantidad debe ser mayor a 0');
      return;
    }

    try {
      const response = await api.post(`/purchase-orders-fundicion/${orderId}/lines/${lineId}/receive`, {
        qty_received: qty,
      });
      showSuccess('Línea recibida correctamente');

      if (response.data.stockChanges && response.data.stockChanges.length > 0) {
        setStockChanges(response.data.stockChanges);
        setIsStockChangeModalOpen(true);
      }

      loadOrders();
      if (selectedOrder) {
        await refreshDetailOrder(orderId);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir línea');
    }
  };

  const toggleDetailLineSelection = (lineId: number, pending: number) => {
    const newSet = new Set(detailSelectedLines);
    if (newSet.has(lineId)) {
      newSet.delete(lineId);
    } else {
      newSet.add(lineId);
      if (!lineQtyInputs.has(lineId)) {
        const newInputs = new Map(lineQtyInputs);
        newInputs.set(lineId, pending.toString());
        setLineQtyInputs(newInputs);
      }
    }
    setDetailSelectedLines(newSet);
  };

  const toggleSelectAllDetailLines = () => {
    if (!selectedOrder) return;
    const pendingIds = selectedOrder.lines
      .filter((line) => line.qty_ordered > line.qty_received)
      .map((line) => line.id);
    if (pendingIds.length === 0) {
      setDetailSelectedLines(new Set());
      return;
    }
    const allSelected = pendingIds.every((id) => detailSelectedLines.has(id));
    if (allSelected) {
      setDetailSelectedLines(new Set());
    } else {
      setDetailSelectedLines(new Set(pendingIds));
    }
  };

  const handleReceiveSelectedLines = async () => {
    if (!selectedOrder || detailSelectedLines.size === 0 || isReceivingSelected) {
      if (!isReceivingSelected) {
        showError('Selecciona al menos una línea pendiente');
      }
      return;
    }
    const orderId = selectedOrder.id;
    try {
      setIsReceivingSelected(true);
      const linesToReceive = [];
      for (const lineId of Array.from(detailSelectedLines)) {
        const line = selectedOrder.lines.find((l) => l.id === lineId);
        if (!line) continue;
        const pending = line.qty_ordered - line.qty_received;
        if (pending <= 0) continue;
        const qtyValue = lineQtyInputs.get(lineId) || pending.toString();
        const qty = Math.min(parseInt(qtyValue, 10) || pending, pending);
        if (qty > 0) {
          linesToReceive.push({ lineId, qty_received: qty });
        }
      }

      if (linesToReceive.length > 0) {
        const response = await api.post(`/purchase-orders-fundicion/${orderId}/receive-all`, {
          lines: linesToReceive,
        });
        showSuccess('Líneas seleccionadas recibidas correctamente');

        if (response.data.stockChanges && response.data.stockChanges.length > 0) {
          setStockChanges(response.data.stockChanges);
          setIsStockChangeModalOpen(true);
        }

        loadOrders();
        await refreshDetailOrder(orderId);

        // Cerrar el modal después de recibir
        setIsDetailModalOpen(false);
        setSelectedOrder(null);
        setDetailSelectedLines(new Set());
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir líneas seleccionadas');
    } finally {
      setIsReceivingSelected(false);
    }
  };

  const handleReceiveAllLines = async () => {
    if (!selectedOrder || isReceivingAll) return;
    const orderId = selectedOrder.id;
    const pendingLines = selectedOrder.lines.filter((line) => line.qty_ordered > line.qty_received);
    if (pendingLines.length === 0) {
      showError('No hay líneas pendientes por recibir');
      return;
    }

    try {
      setIsReceivingAll(true);
      const linesToReceive = pendingLines.map((line) => {
        const pending = line.qty_ordered - line.qty_received;
        return { lineId: line.id, qty_received: pending };
      });

      if (linesToReceive.length > 0) {
        const response = await api.post(`/purchase-orders-fundicion/${orderId}/receive-all`, {
          lines: linesToReceive,
        });
        showSuccess('Todas las líneas pendientes se recibieron correctamente');

        if (response.data.stockChanges && response.data.stockChanges.length > 0) {
          setStockChanges(response.data.stockChanges);
          setIsStockChangeModalOpen(true);
        }

        loadOrders();
        await refreshDetailOrder(orderId);

        // Cerrar el modal después de recibir todo
        setIsDetailModalOpen(false);
        setSelectedOrder(null);
        setDetailSelectedLines(new Set());
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir todas las líneas');
    } finally {
      setIsReceivingAll(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este pedido?')) {
      return;
    }

    try {
      await api.delete(`/purchase-orders-fundicion/${id}`);
      showSuccess('Pedido eliminado correctamente');
      loadOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pedido');
    }
  };

  const handleCloseOrder = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres cerrar manualmente este pedido? El stock pendiente dejará de aparecer como esperado.')) {
      return;
    }

    try {
      await api.post(`/purchase-orders-fundicion/${id}/close`);
      showSuccess('Pedido cerrado correctamente');
      loadOrders();
      loadNeeds();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cerrar pedido');
    }
  };

  const handleDownloadPDF = async (orderId: number, orderCode: string) => {
    try {
      const response = await api.get(`/purchase-orders-fundicion/${orderId}/pdf`, {
        responseType: 'blob',
      });

      // Verificar si la respuesta es un error (JSON en lugar de PDF)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.error || 'Error al generar PDF');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pedido-fundicion-${orderCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('PDF generado correctamente');
    } catch (err: any) {
      let errorMessage = 'Error al generar PDF';

      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = err.message || errorMessage;
          }
        } else {
          errorMessage = err.response.data.error || errorMessage;
        }
      }

      showError(errorMessage);
    }
  };

  const handleOpenEmailModal = (order: PurchaseOrderFundicionWithLines) => {
    setSelectedOrder(order);
    setEmailFormData({
      ...emailFormData,
      subject: `Nº de Pedido de Fundicion ${order.code}`
    });
    setIsEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedOrder) return;

    try {
      await api.post(`/purchase-orders-fundicion/${selectedOrder.id}/email`, emailFormData);
      showSuccess('Email enviado correctamente');
      setIsEmailModalOpen(false);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al enviar email');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'var(--warning)';
      case 'RECEIVED':
        return 'var(--success)';
      case 'CANCELLED':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'RECEIVED':
        return 'Recibido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const pendingDetailLines =
    selectedOrder?.lines.filter((line) => line.qty_ordered > line.qty_received) || [];
  const allDetailLinesSelected =
    pendingDetailLines.length > 0 &&
    pendingDetailLines.every((line) => detailSelectedLines.has(line.id));

  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <EmptyStateError message={error} onRetry={loadOrders} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Pedidos de Fundición</h2>
        {canEdit('fundicion') && (
          <button className="btn btn-primary" onClick={handleOpenCreateModal}>
            + Nuevo Pedido
          </button>
        )}
      </div>


      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Fundidor</th>
              <th>Estado</th>
              <th>Líneas</th>
              <th>Fecha Creación</th>
              <th>Fecha Esperada</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-0">
                  <EmptyStateNoData onCreate={canEdit('fundicion') ? handleOpenCreateModal : undefined} />
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.code}</strong></td>
                  <td>{order.fundidor}</td>
                  <td>
                    <span style={{ color: getStatusColor(order.status) }}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    {order.lines.length} línea(s)
                    <br />
                    <small style={{ color: 'var(--text-secondary)' }}>
                      {order.lines.reduce((sum, l) => sum + l.qty_ordered, 0)} unidades
                    </small>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {order.expected_at
                      ? formatDate(order.expected_at)
                      : '-'}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleOpenDetailModal(order)}
                        title="Ver"
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleDownloadPDF(order.id, order.code)}
                        title="Generar PDF"
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenEmailModal(order)}
                        title="Enviar por Email"
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <Mail size={16} />
                      </button>
                      {canEdit('fundicion') && order.status === 'PENDING' && (
                        <button
                          onClick={() => handleCloseOrder(order.id)}
                          title="Cerrar Manualmente"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--success)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--success)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--success)';
                          }}
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {canEdit('fundicion') && (
                        <button
                          onClick={() => handleDelete(order.id)}
                          title="Eliminar"
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Crear */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nuevo Pedido de Fundición"
        size="large"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateOrder}
              disabled={selectedLines.size === 0 || !formData.fundidor.trim()}
              data-modal-default="true"
            >
              Crear Pedido
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Fundidor *</label>
          <input
            type="text"
            className={`input ${!formData.fundidor.trim() ? 'input-error' : ''}`}
            value={formData.fundidor}
            onChange={(e) => setFormData({ ...formData, fundidor: e.target.value })}
            required
            placeholder="Nombre del fundidor"
          />
        </div>

        <div className="input-group">
          <label className="input-label">Fecha Esperada</label>
          <input
            type="date"
            className="input"
            value={formData.expected_at}
            onChange={(e) => setFormData({ ...formData, expected_at: e.target.value })}
          />
        </div>

        <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Líneas del Pedido</h3>

        {/* Filtro por tipo de pieza */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
            Filtrar por tipo de pieza:
          </label>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { value: 'ANILLO', label: 'Anillos', color: '#3b82f6' },
              { value: 'COLGANTE', label: 'Colgantes', color: '#a855f7' },
              { value: 'PULSERA', label: 'Pulseras', color: '#10b981' },
              { value: 'REASA', label: 'Resas', color: '#f59e0b' },
            ].map((type) => (
              <label
                key={type.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  backgroundColor: selectedPieceTypes.has(type.value) ? `${type.color}20` : 'transparent',
                  border: `2px solid ${selectedPieceTypes.has(type.value) ? type.color : 'var(--border-color)'}`,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!selectedPieceTypes.has(type.value)) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedPieceTypes.has(type.value)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPieceTypes.has(type.value)}
                  onChange={() => togglePieceType(type.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{
                  color: selectedPieceTypes.has(type.value) ? type.color : 'var(--text-secondary)',
                  fontWeight: selectedPieceTypes.has(type.value) ? 'bold' : 'normal'
                }}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <SearchableSelect<MetalPiece>
            items={filteredMetalPieces}
            onSelect={handleManualAddPiece}
            getItemLabel={(item) => item.nombre_pieza}
            getItemId={(item) => item.id}
            getItemCode={(item) => item.cod_pieza}
            placeholder="Buscar pieza por código o nombre para agregar manualmente..."
            label="Agregar Pieza Manualmente"
            excludeIds={new Set([...selectedLines.keys()])}
          />
        </div>

        {visibleNeeds.length > 0 ? (
          <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 100, background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={allNeedsSelected}
                      disabled={selectableNeedIds.length === 0}
                      onChange={toggleSelectAllNeeds}
                    />
                  </th>
                  <th style={{ minWidth: '180px', backgroundColor: 'var(--bg-secondary)' }}>Código</th>
                  <th style={{ minWidth: '300px', backgroundColor: 'var(--bg-secondary)' }}>Nombre</th>
                  <th style={{ minWidth: '100px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)' }}>Material</th>
                  <th style={{ minWidth: '200px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)' }}>Stock Origen<br /><small>(Actual · Mínimo)</small></th>
                  <th style={{ minWidth: '120px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)' }}>Necesario</th>
                  <th style={{ minWidth: '140px', textAlign: 'center', backgroundColor: 'var(--bg-secondary)' }}>Óptimo<br /><small>(Necesario +10%)</small></th>
                  <th style={{ minWidth: '150px', backgroundColor: 'var(--bg-secondary)' }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {visibleNeeds.map((need) => {
                  const isSelected = selectedLines.has(need.metal_piece_id);
                  // Pre-llenar con la cantidad sugerida (needed) cuando se selecciona
                  const qty = selectedLines.get(need.metal_piece_id) ?? (isSelected ? need.needed : 0);
                  return (
                    <tr key={need.metal_piece_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleLine(need)}
                        />
                      </td>
                      <td>
                        <strong
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: need.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                            color: need.material_base === 'LATON' ? '#78350f' : '#1f2937',
                          }}
                        >
                          {need.cod_pieza}
                        </strong>
                      </td>
                      <td>{need.nombre_pieza}</td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: getStockColor(need.current_stock_taller, need.min_stock_taller) }}>
                          {need.current_stock_taller}
                        </strong>
                        {shouldShowAlert(need.current_stock_taller, need.min_stock_taller) && (
                          <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                            ⚠️
                          </span>
                        )}
                        <br />
                        <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                          {need.min_taller_only !== undefined && need.downstream_need !== undefined ? (
                            <>
                              (mín taller: <strong style={{ color: 'var(--primary)' }}>{need.min_taller_only}</strong>
                              {need.downstream_need > 0 && (
                                <> + <span style={{ color: 'var(--warning)' }}>{need.downstream_need}</span> downstream</>
                              )}
                              {need.downstream_need > 0 && (
                                <> = <strong style={{ color: 'var(--text-primary)' }}>{need.min_stock_taller}</strong></>
                              )}
                              {need.downstream_need === 0 && (
                                <> = <strong style={{ color: 'var(--text-primary)' }}>{need.min_stock_taller}</strong></>
                              )}
                              )
                            </>
                          ) : (
                            `(mín: ${need.min_stock_taller})`
                          )}
                        </small>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <strong style={{ color: 'var(--error)' }}>{need.needed}</strong>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const optimalIncrease = Math.ceil(need.needed * 1.1);
                          const stockOptimo = need.current_stock_taller + optimalIncrease;
                          return (
                            <>
                              <strong style={{ color: getStockColor(stockOptimo, need.min_stock_taller) }}>
                                {stockOptimo}
                              </strong>
                              {shouldShowAlert(stockOptimo, need.min_stock_taller) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  ⚠️
                                </span>
                              )}
                              <br />
                              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                (+{optimalIncrease})
                              </small>
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="input"
                          value={isSelected ? qty : ''}
                          onChange={(e) =>
                            handleUpdateQty(need.metal_piece_id, parseInt(e.target.value, 10) || 0)
                          }
                          disabled={!isSelected}
                          placeholder={isSelected ? '' : need.needed.toString()}
                          style={{ width: '120px', minWidth: '120px' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 100, background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={allPiecesSelected}
                      disabled={selectablePieceIds.length === 0}
                      onChange={toggleSelectAllPieces}
                    />
                  </th>
                  <th style={{ minWidth: '180px', backgroundColor: 'var(--bg-secondary)' }}>Código</th>
                  <th style={{ minWidth: '300px', backgroundColor: 'var(--bg-secondary)' }}>Nombre</th>
                  <th style={{ minWidth: '150px', backgroundColor: 'var(--bg-secondary)' }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {filteredMetalPieces.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-0">
                      <EmptyStateNoData />
                    </td>
                  </tr>
                ) : (
                  filteredMetalPieces.map((piece) => {
                    const isSelected = selectedLines.has(piece.id);
                    const qty = selectedLines.get(piece.id) ?? (isSelected ? 10 : 0);
                    return (
                      <tr key={piece.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleMetalPiece(piece)}
                          />
                        </td>
                        <td>
                          <strong
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                              color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                            }}
                          >
                            {piece.cod_pieza}
                          </strong>
                        </td>
                        <td>{piece.nombre_pieza}</td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            className="input"
                            value={isSelected ? qty : ''}
                            onChange={(e) =>
                              handleUpdateQty(piece.id, parseInt(e.target.value, 10) || 0)
                            }
                            disabled={!isSelected}
                            placeholder={isSelected ? '' : '10'}
                            style={{ width: '120px', minWidth: '120px' }}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {selectedLines.size > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <strong>Total seleccionado: {selectedLines.size} línea(s)</strong>
            <br />
            <small style={{ color: 'var(--text-secondary)' }}>
              {Array.from(selectedLines.values()).reduce((sum, qty) => sum + qty, 0)} unidades
            </small>
          </div>
        )}
      </Modal>

      {/* Modal Enviar Email */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title="Enviar Pedido por Email"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setIsEmailModalOpen(false)}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSendEmail}>
              Enviar Email
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Destinatario</label>
          <input
            type="email"
            className="input"
            value={emailFormData.to}
            onChange={(e) => setEmailFormData({ ...emailFormData, to: e.target.value })}
            placeholder="ejemplo@correo.com"
          />
        </div>
        <div className="input-group">
          <label className="input-label">CC (Copia)</label>
          <input
            type="email"
            className="input"
            value={emailFormData.cc}
            onChange={(e) => setEmailFormData({ ...emailFormData, cc: e.target.value })}
            placeholder="ejemplo@correo.com"
          />
        </div>
        <div className="input-group">
          <label className="input-label">Asunto</label>
          <input
            type="text"
            className="input"
            value={emailFormData.subject}
            onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
            placeholder="Asunto del correo"
          />
        </div>
        <div className="input-group">
          <label className="input-label">Mensaje</label>
          <textarea
            className="input"
            rows={8}
            value={emailFormData.body}
            onChange={(e) => setEmailFormData({ ...emailFormData, body: e.target.value })}
            style={{ resize: 'vertical' }}
          />
        </div>
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            📎 Se adjuntará automáticamente el PDF del pedido: <strong>pedido-fundicion-{selectedOrder?.code}.pdf</strong>
          </p>
        </div>
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrder(null);
        }}
        title={selectedOrder ? `Pedido: ${selectedOrder.code}` : 'Detalle'}
        size="large"
        footer={
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', width: '100%' }}>
            {selectedOrder &&
              selectedOrder.status !== 'RECEIVED' &&
              pendingDetailLines.length > 0 && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={handleReceiveSelectedLines}
                    disabled={detailSelectedLines.size === 0 || isReceivingSelected || isReceivingAll}
                  >
                    {isReceivingSelected ? 'Recibiendo...' : 'Recibir seleccionados'}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleReceiveAllLines}
                    disabled={isReceivingAll || isReceivingSelected}
                  >
                    {isReceivingAll ? 'Recibiendo...' : 'Recibir todo'}
                  </button>
                  <button
                    className="btn btn-error"
                    style={{ backgroundColor: 'var(--error)', color: 'white' }}
                    onClick={() => {
                      handleCloseOrder(selectedOrder.id);
                      setIsDetailModalOpen(false);
                    }}
                  >
                    Cerrar Pedido
                  </button>
                </>
              )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsDetailModalOpen(false);
                setSelectedOrder(null);
                setDetailSelectedLines(new Set());
              }}
            >
              Cerrar
            </button>
          </div>
        }
      >
        {selectedOrder && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <p><strong>Fundidor:</strong> {selectedOrder.fundidor}</p>
              <p>
                <strong>Estado:</strong>{' '}
                <span style={{ color: getStatusColor(selectedOrder.status) }}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </p>
              <p>
                <strong>Fecha Creación:</strong>{' '}
                {formatDateTime(selectedOrder.created_at)}
              </p>
              {selectedOrder.expected_at && (
                <p>
                  <strong>Fecha Esperada:</strong>{' '}
                  {formatDateTime(selectedOrder.expected_at)}
                </p>
              )}
            </div>

            <h3 style={{ marginBottom: '1rem' }}>Líneas</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={allDetailLinesSelected}
                        disabled={pendingDetailLines.length === 0}
                        onChange={toggleSelectAllDetailLines}
                      />
                    </th>
                    <th>Pieza</th>
                    <th>Pedido</th>
                    <th>Recibido</th>
                    <th>Pendiente</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.lines.map((line) => {
                    const pending = line.qty_ordered - line.qty_received;
                    const qtyInput = lineQtyInputs.get(line.id) ?? pending.toString();
                    return (
                      <tr key={line.id}>
                        <td>
                          {pending > 0 ? (
                            <input
                              type="checkbox"
                              checked={detailSelectedLines.has(line.id)}
                              onChange={() => toggleDetailLineSelection(line.id, pending)}
                            />
                          ) : null}
                        </td>
                        <td>
                          <strong
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: line.metal_piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                              color: line.metal_piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                            }}
                          >
                            {line.metal_piece.cod_pieza}
                          </strong>
                          <br />
                          <small style={{ color: 'var(--text-secondary)' }}>
                            {line.metal_piece.nombre_pieza}
                          </small>
                        </td>
                        <td>{line.qty_ordered}</td>
                        <td>
                          <strong style={{ color: 'var(--success)' }}>{line.qty_received}</strong>
                        </td>
                        <td>
                          <strong style={{ color: pending > 0 ? 'var(--warning)' : 'var(--success)' }}>
                            {pending}
                          </strong>
                        </td>
                        <td>
                          {pending > 0 && selectedOrder.status !== 'RECEIVED' ? (
                            <div className="flex gap-1" style={{ alignItems: 'center' }}>
                              <input
                                type="number"
                                min="1"
                                max={pending}
                                className="input"
                                value={qtyInput}
                                onChange={(e) => {
                                  const newMap = new Map(lineQtyInputs);
                                  newMap.set(line.id, e.target.value);
                                  setLineQtyInputs(newMap);
                                }}
                                placeholder="Cantidad"
                                style={{ width: '80px' }}
                              />
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  const qty = parseInt(qtyInput, 10);
                                  if (qty > 0 && qty <= pending) {
                                    handleReceiveLine(selectedOrder.id, line.id, qty);
                                    const newMap = new Map(lineQtyInputs);
                                    newMap.delete(line.id);
                                    setLineQtyInputs(newMap);
                                  }
                                }}
                                style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                              >
                                Recibir
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>Completo</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sección de Comentarios */}
        {selectedOrder && (
          <div style={{ marginTop: '2rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
            <CommentsSection
              entityType="PURCHASE_ORDER_FUNDICION"
              entityId={selectedOrder.id}
            />
          </div>
        )}
      </Modal>

      <StockChangeModal
        isOpen={isStockChangeModalOpen}
        onClose={() => setIsStockChangeModalOpen(false)}
        title="Cambios de Stock - Pedido de Fundición Recibido"
        changes={stockChanges}
      />
    </div>
  );
}
